import { makeObservable, observable } from 'mobx';
import type { Aggregate } from 'edit/Aggregate';
import type { SchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { formatSchedulingWindowPath, parseSchedulingWindowPath } from 'edit/reference/schedulingWindowPath';
import type { Recurrence, RecurrenceUnit } from 'api/recurrence_definitions/endpoints/types';
import type { EditableRecurrenceDefinition } from './EditableRecurrenceDefinition';

export type { RecurrenceUnit };

/**
 * The radio choice between an interval rule ("every N units") and a
 * window-open rule. Kept decoupled from `unit`: `mode` is the radio, `unit` is
 * the interval-unit dropdown. The wire collapses both into a single `unit`
 * field (where `window_open` is one of the values); `toSave` resolves the
 * asymmetry. Decoupling lets the user toggle mode without losing the interval
 * unit they previously picked.
 */
export type RecurrenceMode = 'interval' | 'window_open';

/**
 * UI-side type for the interval-mode unit dropdown: every recurrence unit
 * except `window_open` (which `mode` represents instead). Exposing it as a
 * sibling of `RecurrenceUnit` makes the field's intent explicit — `unit` is
 * the chosen interval unit, never `window_open`.
 */
export type IntervalRecurrenceUnit = Exclude<RecurrenceUnit, 'window_open'>;

// Monotonic source of stable per-instance keys for React list rendering. New
// recurrences have no server id, and array index is unstable across add/remove,
// so each instance gets a unique key at construction that never changes.
let nextRecurrenceKey = 0;

const DEFAULT_UNIT: IntervalRecurrenceUnit = 'day';
const DEFAULT_INTERVAL = 1;
const DEFAULT_PRESERVE_TIME = true;
const DEFAULT_SKIP_WHEN_WINDOW_CLOSED = true;

/**
 * One recurrence rule, as a reactive child of an `EditableRecurrenceDefinition`.
 *
 * The parent→child relationship is owned by a shared `Aggregate` that both
 * sides hold: this child routes its back-reference and self-delete through it,
 * so the parent never has to expose array-mutation methods. Every user-fillable
 * field is a public observable, assigned directly from the form.
 *
 * There is intentionally no cross-field logic on the fields. Values typed in
 * the non-current `mode` branch are preserved by the disabled-fieldset form
 * control, so a mode toggle never has to clear or seed a field — `toSave` is
 * where the mode is applied, emitting only the fields that mode implies.
 */
export class EditableRecurrence {
  private readonly _aggregate: Aggregate<EditableRecurrenceDefinition, EditableRecurrence>;
  description: string | null = null;
  duringSchedulingWindow: SchedulingWindowReference | null = null;
  interval: number | null = DEFAULT_INTERVAL;
  /** Stable identity for React list keys; never changes after construction. */
  readonly key = `recurrence-${(nextRecurrenceKey += 1)}`;
  mode: RecurrenceMode = 'interval';
  preserveTime: boolean = DEFAULT_PRESERVE_TIME;
  schedulingWindow: SchedulingWindowReference | null = null;
  skipWhenWindowClosed: boolean = DEFAULT_SKIP_WHEN_WINDOW_CLOSED;
  startTime: string | null = null;
  startTimeZone: string | null = null;
  unit: IntervalRecurrenceUnit = DEFAULT_UNIT;

  /**
   * @param aggregate the shared parent→children owner; the back-reference and
   *   self-delete route through it.
   * @param wire when present, seeds the fields from a loaded recurrence; when
   *   absent, the field defaults stand (a fresh, empty interval rule).
   */
  constructor(aggregate: Aggregate<EditableRecurrenceDefinition, EditableRecurrence>, wire?: Recurrence) {
    this._aggregate = aggregate;
    makeObservable(this, {
      description: observable,
      duringSchedulingWindow: observable.ref,
      interval: observable,
      mode: observable,
      preserveTime: observable,
      schedulingWindow: observable.ref,
      skipWhenWindowClosed: observable,
      startTime: observable,
      startTimeZone: observable,
      unit: observable,
    });
    if (wire !== undefined) {
      this.description = wire.description ?? null;
      // A window_open wire has no interval unit to remember — fall back to the
      // default so the dropdown is sane if the user toggles mode to interval.
      this.mode = wire.unit === 'window_open' ? 'window_open' : 'interval';
      this.unit = wire.unit === 'window_open' ? DEFAULT_UNIT : wire.unit;
      this.interval = wire.interval ?? DEFAULT_INTERVAL;
      this.schedulingWindow = parseSchedulingWindowPath(wire.scheduling_window);
      this.duringSchedulingWindow = parseSchedulingWindowPath(wire.during_scheduling_window);
      this.preserveTime = wire.preserve_time ?? DEFAULT_PRESERVE_TIME;
      this.skipWhenWindowClosed = wire.skip_when_window_closed ?? DEFAULT_SKIP_WHEN_WINDOW_CLOSED;
      this.startTime = wire.start_time ?? null;
      this.startTimeZone = wire.start_time_zone ?? null;
    }
  }

  /**
   * Human-readable summary of this rule, or `null` when it isn't complete
   * enough to phrase. Presentational helper; the dialog renders it when set.
   */
  get explanation(): string | null {
    if (this.mode === 'window_open') {
      if (this.schedulingWindow === null) return null;
      return `Executes whenever the scheduling window "${this.schedulingWindow.name}" opens.`;
    }
    if (this.interval === null) return null;
    return `Executes every ${this.interval} ${this.unit}(s).`;
  }

  /** Back-reference to the owning definition, via the shared aggregate. */
  get recurrenceDefinition(): EditableRecurrenceDefinition {
    return this._aggregate.parent;
  }

  /** Remove this recurrence from its definition. */
  delete = (): void => {
    this._aggregate.remove(this);
  };

  /**
   * Project to the wire shape, emitting only the fields the chosen mode
   * implies. The other mode's fields keep their editable values (the disabled
   * fieldset preserves them for a toggle back) but are omitted from the body.
   *
   * That omission is load-bearing, not tidiness: the server rejects a field the
   * unit doesn't use rather than ignoring it.
   *
   * Server-assigned `sort_order` / id are deliberately omitted (the definition
   * conveys order by array position).
   */
  toSave(): Recurrence {
    const isWindowOpen = this.mode === 'window_open';
    return {
      description: this.description ?? undefined,
      unit: isWindowOpen ? 'window_open' : this.unit,
      interval: isWindowOpen ? undefined : (this.interval ?? undefined),
      scheduling_window: isWindowOpen ? (formatSchedulingWindowPath(this.schedulingWindow) ?? undefined) : undefined,
      during_scheduling_window: isWindowOpen ? undefined : (formatSchedulingWindowPath(this.duringSchedulingWindow) ?? undefined),
      preserve_time: isWindowOpen ? undefined : this.preserveTime,
      skip_when_window_closed: this.skipWhenWindowClosed,
      start_time: isWindowOpen ? undefined : (this.startTime ?? undefined),
      start_time_zone: isWindowOpen ? undefined : (this.startTimeZone ?? undefined),
    };
  }
}
